import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/order-sample", label: "Order Sample" },
  { to: "/categories", label: "Categories" },
  { to: "/manufacturing", label: "Manufacturing" },
  { to: "/pli", label: "PLI", badge: "Completed" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={cn(
        "fixed top-0 inset-x-0 z-50 transition-all duration-500",
        scrolled
          ? "bg-[#F7F4EE]/85 backdrop-blur-xl border-b border-[#D7D4CF]/50"
          : "bg-transparent",
      )}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="navbar-logo"
          className="flex items-center gap-2 group"
        >
          <img
            src="/images/fresio-logo.png"
            alt="Fresio Herbals"
            className="h-8 lg:h-9 w-auto"
            draggable="false"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                cn(
                  "relative text-[13px] tracking-[0.15em] uppercase font-medium transition-colors duration-300 flex items-center gap-2",
                  isActive
                    ? "text-[#1D3A2F]"
                    : "text-[#222222]/60 hover:text-[#1D3A2F]",
                  l.badge && "text-[#1D3A2F]",
                )
              }
            >
              {l.label}
              {l.badge && (
                <span
                  data-testid="nav-pli-badge"
                  className="relative inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#1D3A2F] text-[#FDFBF7] text-[9px] tracking-[0.18em] font-semibold"
                >
                  <span className="relative flex w-1.5 h-1.5">
                    <span className="absolute inset-0 rounded-full bg-[#FDFBF7] animate-ping opacity-75" />
                    <span className="relative w-1.5 h-1.5 rounded-full bg-[#FDFBF7]" />
                  </span>
                  {l.badge}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="navbar-cta-get-in-touch"
            className="hidden lg:inline-flex items-center gap-2 h-11 px-6 rounded-full bg-[#1D3A2F] text-[#FDFBF7] text-[13px] tracking-[0.14em] uppercase font-medium hover:bg-black transition-colors duration-500"
          >
            Get In Touch
          </Link>
          <button
            data-testid="navbar-mobile-toggle"
            className="lg:hidden w-11 h-11 flex items-center justify-center rounded-full border border-[#D7D4CF]"
            aria-label="Menu"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#F7F4EE] border-t border-[#D7D4CF]/60">
          <div className="max-w-[1400px] mx-auto px-6 py-6 flex flex-col gap-4">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  cn(
                    "text-lg font-editorial flex items-center gap-3",
                    isActive ? "text-[#1D3A2F]" : "text-[#222222]",
                  )
                }
              >
                {l.label}
                {l.badge && (
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#1D3A2F] text-[#FDFBF7] text-[9px] tracking-[0.18em] font-semibold font-sans-luxe uppercase">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#FDFBF7] animate-pulse" />
                    {l.badge}
                  </span>
                )}
              </NavLink>
            ))}
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-[#1D3A2F] text-[#FDFBF7] text-[13px] tracking-[0.14em] uppercase font-medium w-fit"
            >
              Get In Touch
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
